import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface WaitlistEventActionsProps {
  eventId: string;
}

const WaitlistEventActions = ({ eventId }: WaitlistEventActionsProps) => {
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("salon_waitlist")
        .delete()
        .eq("user_id", user.id)
        .eq("event_id", eventId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["salon-waitlist", eventId] });
      toast({
        title: "Request cancelled",
        description: "Your waitlist request has been cancelled.",
      });
    },
    onError: (error) => {
      console.error("Error cancelling request:", error);
      toast({
        title: "Error",
        description: "Failed to cancel request. Please try again.",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="fixed bottom-20 left-0 right-0 bg-amber-50 border-t border-amber-200 p-4">
      <div className="max-w-md mx-auto space-y-2">
        <div className="text-center">
          <p className="font-semibold text-amber-900">
            Request submitted. Keep an eye on your email for updates.
          </p>
          <p className="text-sm text-amber-700 mt-1">
            Note that your spot is not confirmed yet.
          </p>
        </div>
        <Button
          variant="destructive"
          className="w-full"
          onClick={() => cancelMutation.mutate()}
          disabled={cancelMutation.isPending}
        >
          {cancelMutation.isPending ? "Cancelling..." : "Cancel Request"}
        </Button>
      </div>
    </div>
  );
};

export default WaitlistEventActions;
